/**
 * The SIRS special assessment a building's association reported: what it is
 * allowed to say.
 *
 * The record is the association's own answer to DBPR, on the structural
 * integrity reserve study form used before July 2025: *did the study lead to
 * a special assessment, and what did it cost?* So every sentence here names
 * the filing as its source. It is a report of what was filed, not a finding
 * that owners were billed or paid.
 *
 * **The report is the signal; the amounts are context.** The engine drops a
 * figure that cannot be right (typed errors run to billions), and keeps the
 * report. So a building can carry the signal with no per-unit figure, and
 * that is said rather than shown as a dash.
 *
 * **And absence is silence.** All three fields are null where no report is
 * joined to the building, which is most buildings, and is not "none was
 * filed": the join from a filing to a building reaches about a fifth of them.
 */

import type { Building } from "../api/types";
import { money } from "./format";

type Report = Pick<Building, "sirs_assessment_date" | "sirs_assessment_total"
  | "sirs_assessment_per_unit">;

/** Whether a SIRS assessment report is joined to this building. */
export function hasSirsAssessment(building: Report): boolean {
  return building.sirs_assessment_date !== null
    || building.sirs_assessment_total !== null
    || building.sirs_assessment_per_unit !== null;
}

/**
 * The amounts, per unit first because that is what each owner was asked for.
 * "" when there is no report.
 */
export function sirsAssessmentLabel(building: Report): string {
  if (!hasSirsAssessment(building)) return "";
  const perUnit = building.sirs_assessment_per_unit;
  const total = building.sirs_assessment_total;
  const parts = [
    perUnit !== null ? `${money(perUnit)} per unit` : "",
    total !== null ? `${money(total)} total` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "Amount not readable in the filing";
}

/** When the study behind it was completed, or "" if the filing did not say. */
export function sirsAssessmentWhen(building: Report): string {
  const when = formatDay(building.sirs_assessment_date);
  return when ? `Study completed ${when}` : "";
}

/** The sentence saying where this comes from, for a tooltip. */
export function sirsAssessmentBasis(building: Report): string {
  if (!hasSirsAssessment(building)) return "";
  return (
    "The association reported to DBPR, on the structural integrity reserve " +
    "study form used before July 2025, that the study led to a special " +
    "assessment. Amounts are as the association filed them; a figure that " +
    "could not be right is left out."
  );
}

/**
 * "2 Jul 2024". Parsed by hand for `formatPermitDate`'s reason: `new Date`
 * reads a bare `YYYY-MM-DD` as UTC midnight and shows everyone west of
 * Greenwich the day before.
 */
export function formatDay(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? "").trim());
  if (!match) return "";
  const [, year, month, day] = match;
  const index = Number(month) - 1;
  if (index < 0 || index > 11 || Number(day) < 1 || Number(day) > 31) return "";
  return `${Number(day)} ${MONTHS[index]} ${year}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
